import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import ReportsService from 'projects/api/ReportsService';
import { asErrorData, DataState } from 'slices';
import { ErrorData, GenericDataState } from 'type';
import { PendingReport, PendingReportInfo, ReportData, ReportListItem } from 'types/report';

const EMPTY_DATA: any[] = [];

type ReportsDataState = GenericDataState<ReportListItem[]> & {
  total: number;
  queryParams?: {
    projectId: string;
  };
  pendingReport: GenericDataState<PendingReport>;
  reportData: GenericDataState<ReportData>;
};

export const getReportsList = createAsyncThunk(
  'reports/getReportsList',
  async (
    params: {
      context: any;
      queryParams: {
        projectId: string;
      }
    },
    thunkApi
  ) => {
    const { queryParams } = params;
    try {
      const result = await ReportsService.getReports({
        projectId: queryParams.projectId,
      });
      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const getPendingReport = createAsyncThunk(
  'reports/getPendingReport',
  async (
    projectId: string,
    thunkApi
  ) => {
    try {
      const result = await ReportsService.getPendingReport(projectId);
      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const getReportData = createAsyncThunk(
  'reports/getReportData',
  async (
    params: {
      context: any;
      id: string;
    },
    thunkApi
  ) => {
    const { id } = params;
    try {
      const result = await ReportsService.getReportData(id);
      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

const initialState: ReportsDataState = {
  data: [],
  total: 0,
  queryParams: undefined,
  dataState: DataState.INITIAL,
  pendingReport: {
    data: {
      areAllReportsGenerated: false,
      pendingReportInfo: {} as PendingReportInfo
    },
    dataState: DataState.INITIAL,
  },
  reportData: {
    dataState: DataState.INITIAL,
    data: undefined,
  },
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setQueryParams: (state, action) => {
      state.queryParams = action.payload;
    },
    expireReports: (state, action: { payload: { expireData: boolean; }; }) => {
      if (action?.payload?.expireData) {
        state.data = [];
        state.total = 0;
        state.queryParams = undefined;
        state.pendingReport.data = {
          areAllReportsGenerated: false,
          pendingReportInfo: {} as PendingReportInfo,
        };
        state.reportData.data = undefined;
      }
      state.dataState = DataState.INITIAL;
      state.pendingReport.dataState = DataState.INITIAL;
      state.reportData.dataState = DataState.INITIAL;
    },
    expirePendingReport: (state) => {
      state.pendingReport.data = {
        areAllReportsGenerated: false,
        pendingReportInfo: {} as PendingReportInfo,
      };
      state.pendingReport.dataState = DataState.INITIAL;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getReportsList.pending, (state) => {
      state.data = [];
      state.total = 0;
      state.dataState = DataState.LOADING;
    });
    builder.addCase(getReportsList.fulfilled, (state, action) => {
      state.dataState = DataState.FULFILLED;
      state.data = action.payload;
      state.total = action.payload.length;
    });
    builder.addCase(getReportsList.rejected, (state, action) => {
      if (action.payload) {
        state.error = action.payload as ErrorData;
      } else {
        state.error = asErrorData(action.error);
      }
      state.dataState = DataState.ERROR;
    });
    builder.addCase(getReportData.pending, (state) => {
      state.reportData.dataState = DataState.LOADING;
    });
    builder.addCase(getReportData.fulfilled, (state, action) => {
      state.reportData.dataState = DataState.FULFILLED;
      state.reportData.data = action.payload;
    });
    builder.addCase(getReportData.rejected, (state, action) => {
      if (action.payload) {
        state.error = action.payload as ErrorData;
      } else {
        state.error = asErrorData(action.error);
      }
      state.reportData.dataState = DataState.ERROR;
    });
    builder.addCase(getPendingReport.pending, (state) => {
      state.pendingReport.dataState = DataState.LOADING;
    });
    builder.addCase(getPendingReport.fulfilled, (state, action) => {
      state.pendingReport.dataState = DataState.FULFILLED;
      state.pendingReport.data = action.payload;
    });
    builder.addCase(getPendingReport.rejected, (state, action) => {
      if (action.payload) {
        state.error = action.payload as ErrorData;
      } else {
        state.error = asErrorData(action.error);
      }
      state.pendingReport.dataState = DataState.ERROR;
    });
  },
});

export default reportsSlice.reducer;

export const selectReports = (state: { reports2: ReportsDataState }): ReportListItem[] => state.reports2.data ?? EMPTY_DATA;
export const selectAbridgeMainReports = createSelector([state => state.reports2.data], (reports: ReportListItem[]) => {
  return (reports ?? EMPTY_DATA as ReportListItem[]).filter(report => report.type === "Main" || report.type === "Abridged");
});
export const selectLocations = createSelector([state => state.reports2.reportData.data], (reportData: ReportData) => {
  return reportData?.locations || [];
});
export const selectReportsDataState = (state: { reports2: ReportsDataState }): DataState => state.reports2.dataState;
export const selectReportsTotal = (state: { reports2: ReportsDataState }) => state.reports2.total;
export const selectReportData = (state: { reports2: ReportsDataState }) => state.reports2.reportData.data;
export const selectReportDataState = (state: { reports2: ReportsDataState }) => state.reports2.reportData.dataState;
export const selectPendingReportInfo = (state: { reports2: ReportsDataState }) => state.reports2.pendingReport.data;
export const selectPendingReportInfoDataState = (state: { reports2: ReportsDataState }) => state.reports2.pendingReport.dataState;

export const { expireReports, expirePendingReport } = reportsSlice.actions;
